// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';

import { PlayingStyleView } from './PlayingStyleView';
import type { TourPlayingStyleEntry } from './types';

type Props = {
  /** The player whose tour profile is open; null keeps the modal closed. */
  player: { name: string; entry: TourPlayingStyleEntry } | null;
  fieldSize: number;
  onClose: () => void;
};

/** A player's profile for one tour, opened from their row on the tour leaderboard. */
export function TourPlayingStyleModal({ player, fieldSize, onClose }: Props) {
  const { t } = useI18n();

  return (
    <Modal visible={player !== null} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={(event) => event.stopPropagation()} testID="tour-playing-style-modal">
          {player ? (
            <>
              <Text style={styles.title}>{t('tourPlayingStyle.profileTitle', { name: player.name })}</Text>
              <ScrollView contentContainerStyle={styles.content}>
                {player.entry.traits.length > 0 ? (
                  <PlayingStyleView playingStyle={player.entry} variant="tour" fieldSize={fieldSize} />
                ) : (
                  <Text style={styles.message}>{t('tourPlayingStyle.notEnoughHoles')}</Text>
                )}
              </ScrollView>
              <View style={styles.buttonRow}>
                <GoffyButton
                  label={t('tourPlayingStyle.close')}
                  onPress={onClose}
                  size="compact"
                  testID="tour-playing-style-close"
                />
              </View>
            </>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.6),
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '90%',
    borderRadius: 16,
    padding: 16,
    backgroundColor: appColors.background.app,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    gap: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.heading,
  },
  content: {
    paddingBottom: 4,
  },
  message: {
    fontSize: 14,
    color: appColors.text.secondary,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
});
